import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { AgeBucket } from '../api/types';
import { count } from '../utils/format';

import { ChartTooltip } from './ChartTooltip';
import { Legend } from './Legend';
import { CHROME, FRESHNESS_COLOR } from './palette';

/**
 * How many products sit in each age band of their newest successful scan.
 *
 * The bands are ordered along the axis, so the fill is free to carry what the
 * axis cannot: whether a band still counts as current. Three states, each
 * labelled, so nothing rests on colour alone.
 */
function fill(bucket: AgeBucket): string {
  if (bucket.position === 0) {
    return FRESHNESS_COLOR.missing;
  }

  return bucket.overdue ? FRESHNESS_COLOR.overdue : FRESHNESS_COLOR.fresh;
}

export function FreshnessHistogram({
  buckets,
  rescanAfterHours,
}: {
  buckets: AgeBucket[];
  rescanAfterHours: number;
}) {
  const total = buckets.reduce((sum, bucket) => sum + bucket.products, 0);

  if (total === 0) {
    return (
      <div className="state">No active products in the catalogue yet.</div>
    );
  }

  const rows = buckets.map((bucket) => ({ ...bucket, fill: fill(bucket) }));

  const overdueDays = Math.round(rescanAfterHours / 24);

  return (
    <>
      <ResponsiveContainer width='100%' height={260}>
        <BarChart
          data={rows}
          margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
          barCategoryGap={4}
        >
          <CartesianGrid stroke={CHROME.grid} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: CHROME.inkMuted, fontSize: 10 }}
            axisLine={{ stroke: CHROME.axis }}
            tickLine={false}
            interval={0}
            angle={-28}
            textAnchor="end"
            height={58}
          />
          <YAxis
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={38}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const row = payload[0].payload as (typeof rows)[number];

              return (
                <ChartTooltip
                  title={row.label}
                  rows={[
                    {
                      key: 'products',
                      label: 'Products',
                      value: count(row.products),
                      color: row.fill,
                    },
                    {
                      key: 'share',
                      label: 'Share',
                      value: `${((row.products / total) * 100).toFixed(1)}%`,
                    },
                    {
                      key: 'state',
                      label: 'State',
                      value:
                        row.position === 0
                          ? 'no data'
                          : row.overdue
                            ? 'due a rescan'
                            : 'inside the window',
                    },
                  ]}
                />
              );
            }}
          />
          <Bar
            isAnimationActive={false}
            dataKey="products"
            radius={[4, 4, 0, 0]}
            maxBarSize={64}
          >
            {rows.map((row) => (
              <Cell key={row.label} fill={row.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <Legend
        entries={[
          { label: `Inside the ${overdueDays}-day window`, color: FRESHNESS_COLOR.fresh },
          { label: 'Due a rescan', color: FRESHNESS_COLOR.overdue },
          { label: 'Never scanned', color: FRESHNESS_COLOR.missing },
        ]}
      />
    </>
  );
}
